import { Injectable } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError, Subject } from 'rxjs';
import { catchError, switchMap, take } from 'rxjs/operators';
import { AuthService } from '../services/auth.service';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  // Plain Subject — no replay; waiting requests receive exactly one emit when refresh completes
  private refreshDone$ = new Subject<string>();

  constructor(private auth: AuthService) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const token = this.auth.accessToken;
    const authReq = token ? req.clone({ setHeaders: { Authorization: `Bearer ${token}` } }) : req;

    return next.handle(authReq).pipe(
      catchError((err: HttpErrorResponse) => {
        const isAuthEndpoint = req.url.includes('/auth/login') ||
                               req.url.includes('/auth/refresh') ||
                               req.url.includes('/auth/logout');

        if (isAuthEndpoint) return throwError(() => err);

        if (err.status === 401) {
          if (!this.auth.refreshToken) {
            this.auth.clearSession();
            return throwError(() => err);
          }
          return this.handle401(req, next);
        }

        return throwError(() => err);
      })
    );
  }

  private handle401(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;

      return this.auth.refresh().pipe(
        switchMap(({ accessToken }) => {
          this.isRefreshing = false;
          this.refreshDone$.next(accessToken);
          return next.handle(req.clone({ setHeaders: { Authorization: `Bearer ${accessToken}` } }));
        }),
        catchError(err => {
          this.isRefreshing = false;
          // Unblock any waiting requests before logging out
          this.refreshDone$.error(err);
          this.refreshDone$ = new Subject<string>();
          this.auth.logout();
          return throwError(() => err);
        })
      );
    }

    // Another refresh is in progress — wait for it to complete then retry
    return this.refreshDone$.pipe(
      take(1),
      switchMap(token =>
        next.handle(req.clone({ setHeaders: { Authorization: `Bearer ${token}` } }))
      )
    );
  }
}
